import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Col,
    Descriptions,
    Empty,
    Modal,
    Row,
    Segmented,
    Space,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
    Typography
} from 'antd';
import {
    botService,
    ForecastDetailResponse,
    ForecastEvaluationStatus,
    ForecastSummaryResponse,
    ForecastValidationStatus
} from '../../services/botService';
import LightweightCandlestickChart, {KLineBar} from '../charts/LightweightCandlestickChart';
import {tradingService} from '../../services/tradingService';
import {
    buildForecastConnector,
    canShowModelPreview,
    getTimeframeMs,
    splitValidationMessages
} from './forecastChartUtils';

const {Text} = Typography;
const TOTAL_VISIBLE_BARS = 50;
const MARK_PRICE_POLL_INTERVAL_MS = 5_000;
const CHART_DATA_POLL_INTERVAL_MS = 15_000;

interface KlineForecastModalProps {
    visible: boolean;
    decisionId: string | number;
    onClose: () => void;
}

const validationPresentation: Record<ForecastValidationStatus, { label: string; color: string }> = {
    VALID: {label: '格式有效', color: 'green'},
    INVALID: {label: '格式非法', color: 'red'},
    MISSING: {label: '预测缺失', color: 'orange'}
};

const evaluationPresentation: Record<ForecastEvaluationStatus, { label: string; color: string }> = {
    PENDING: {label: '等待评价', color: 'blue'},
    PARTIAL: {label: '等待完整数据', color: 'cyan'},
    EVALUATING: {label: '评价中', color: 'processing'},
    EVALUATED: {label: '已评价', color: 'green'},
    SKIPPED: {label: '已跳过', color: 'default'},
    FAILED: {label: '评价失败', color: 'red'}
};

const percent = (value?: number | null, ratio = false) => {
    if (value === null || value === undefined) return '-';
    return `${(Number(value) * (ratio ? 100 : 1)).toFixed(2)}%`;
};

const KlineForecastModal: React.FC<KlineForecastModalProps> = ({
                                                                   visible,
                                                                   decisionId,
                                                                   onClose
                                                               }) => {
    const [summaries, setSummaries] = useState<ForecastSummaryResponse[]>([]);
    const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null);
    const [detail, setDetail] = useState<ForecastDetailResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contextBars, setContextBars] = useState<KLineBar[]>([]);
    const [marketActualBars, setMarketActualBars] = useState<KLineBar[]>([]);
    const [currentMarkPrice, setCurrentMarkPrice] = useState<number | null>(null);
    const [markPriceUpdatedAt, setMarkPriceUpdatedAt] = useState<number | null>(null);
    const [markPriceError, setMarkPriceError] = useState<string | null>(null);
    const [showHoldTp, setShowHoldTp] = useState(false);
    const [showInvalidation, setShowInvalidation] = useState(false);
    const [forecastTf, setForecastTf] = useState<'5m' | '1h'>('5m');
    const openedAtRef = useRef(0);

    const activeForecast = useMemo(() => {
        if (!detail) return null;
        return forecastTf === '1h' && detail.forecast1h ? detail.forecast1h : detail.forecast;
    }, [detail, forecastTf]);

    const forecastStartTime = useMemo(() => {
        const firstBar = activeForecast?.bars?.[0];
        return firstBar?.timestamp ?? detail?.evaluationStartTime ?? 0;
    }, [activeForecast, detail?.evaluationStartTime]);

    useEffect(() => {
        if (!visible) return;
        let active = true;
        setLoading(true);
        setError(null);
        setSummaries([]);
        setSelectedForecastId(null);
        setDetail(null);
        setContextBars([]);
        setMarketActualBars([]);
        setCurrentMarkPrice(null);
        setMarkPriceUpdatedAt(null);
        setMarkPriceError(null);
        setShowHoldTp(false);
        setShowInvalidation(false);
        setForecastTf('5m');
        openedAtRef.current = Date.now();
        botService.getForecastsByDecision(decisionId).then(response => {
            if (!active) return;
            if (!response.success) {
                setError(response.message || '获取K线预测失败');
                return;
            }
            const next = response.data || [];
            setSummaries(next);
            setSelectedForecastId(next[0]?.forecastId ?? null);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [visible, decisionId]);

    useEffect(() => {
        if (!visible || selectedForecastId === null) return;
        let active = true;
        setDetailLoading(true);
        setError(null);
        setDetail(null);
        setContextBars([]);
        setMarketActualBars([]);
        setCurrentMarkPrice(null);
        setMarkPriceUpdatedAt(null);
        setMarkPriceError(null);
        setShowHoldTp(false);
        setShowInvalidation(false);
        setForecastTf('5m');
        botService.getForecastById(selectedForecastId).then(response => {
            if (!active) return;
            if (!response.success) {
                setDetail(null);
                setError(response.message || '获取K线预测详情失败');
                return;
            }
            setDetail(response.data);
        }).finally(() => {
            if (active) setDetailLoading(false);
        });
        return () => {
            active = false;
        };
    }, [visible, selectedForecastId]);

    useEffect(() => {
        if (!visible
            || !detail
            || detail.forecastId !== selectedForecastId) return;
        let active = true;
        let timeoutId: number | null = null;

        const updateMarkPrice = async () => {
            try {
                const response = await tradingService.getMarkPrice(
                    detail.instId,
                    detail.apiKeyId,
                    {forceRefresh: true}
                );
                const nextPrice = Number(response?.markPrice);
                if (active && response?.success && Number.isFinite(nextPrice) && nextPrice > 0) {
                    setCurrentMarkPrice(nextPrice);
                    const responseTimestamp = Number(response.timestamp);
                    setMarkPriceUpdatedAt(
                        Number.isFinite(responseTimestamp) && responseTimestamp > 0
                            ? responseTimestamp
                            : Date.now()
                    );
                    setMarkPriceError(null);
                } else if (active) {
                    setMarkPriceError(response?.error || '实时价格响应无效');
                }
            } catch (error) {
                if (active) {
                    setMarkPriceError(
                        error instanceof Error ? error.message : '实时价格请求失败'
                    );
                }
            } finally {
                // 上一次请求完成后再安排下一次，避免慢请求重叠和旧响应覆盖新响应。
                if (active) {
                    timeoutId = window.setTimeout(
                        updateMarkPrice,
                        MARK_PRICE_POLL_INTERVAL_MS
                    );
                }
            }
        };

        void updateMarkPrice();
        return () => {
            active = false;
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [visible, selectedForecastId, detail?.forecastId, detail?.instId, detail?.apiKeyId]);

    useEffect(() => {
        if (!visible || !detail || !selectedForecastId) return;
        const pending = ['PENDING', 'PARTIAL', 'EVALUATING'].includes(detail.evaluationStatus);
        if (!pending || Date.now() - openedAtRef.current >= 40 * 60 * 1000) return;
        const timer = window.setTimeout(async () => {
            const response = await botService.getForecastById(selectedForecastId);
            if (response.success && response.data) {
                setDetail(response.data);
            }
        }, 30_000);
        return () => window.clearTimeout(timer);
    }, [visible, detail, selectedForecastId]);

    useEffect(() => {
        if (!visible || !detail) return;
        let active = true;
        let timeoutId: number | null = null;
        if (!activeForecast) return;
        const predictionCount = activeForecast.horizonBars || 5;
        const contextCount = Math.max(1, TOTAL_VISIBLE_BARS - predictionCount);
        const timeframeMs = getTimeframeMs(activeForecast.timeframe);
        const predictionEndTime = forecastStartTime + predictionCount * timeframeMs;

        const loadChartData = async () => {
            try {
                const chartData = await tradingService.getCompleteChartData({
                    instId: detail.instId,
                    apiKeyId: detail.apiKeyId,
                    timeframe: activeForecast.timeframe || '5m',
                    // 必须同时请求预测前上下文和预测窗口，不能只取 contextCount。
                    limit: TOTAL_VISIBLE_BARS,
                    startMills: forecastStartTime - contextCount * timeframeMs
                });
                if (!active) return;
                const bars = (chartData.candles || []).map(candle => ({
                    time: candle.timestamp,
                    open: Number(candle.open),
                    high: Number(candle.high),
                    low: Number(candle.low),
                    close: Number(candle.close),
                    volume: Number(candle.volume || 0),
                    confirmed: candle.confirm === undefined || candle.confirm === 1
                }));
                setContextBars(bars
                    .filter(candle => Number(candle.time) < forecastStartTime)
                    .slice(-contextCount));
                // 预测评价尚未落库时，直接用行情接口返回的预测窗口实际K线补齐图表。
                setMarketActualBars(bars.filter(candle => {
                    const time = Number(candle.time);
                    return time >= forecastStartTime && time < predictionEndTime;
                }));
            } catch {
                // 单次加载失败时保留上次图表数据，下一轮继续刷新。
            } finally {
                if (active) {
                    timeoutId = window.setTimeout(
                        loadChartData,
                        CHART_DATA_POLL_INTERVAL_MS
                    );
                }
            }
        };

        setContextBars([]);
        setMarketActualBars([]);
        void loadChartData();
        return () => {
            active = false;
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [
        visible,
        detail?.forecastId,
        detail?.instId,
        detail?.apiKeyId,
        forecastStartTime,
        activeForecast?.timeframe,
        activeForecast?.horizonBars
    ]);

    const predictedBars = useMemo<KLineBar[]>(() => {
        if (!activeForecast?.bars) return [];
        const timeframeMs = getTimeframeMs(activeForecast.timeframe);
        return activeForecast.bars.map(bar => ({
            time: bar.timestamp
                ?? forecastStartTime + (bar.index - 1) * timeframeMs,
            open: Number(bar.open),
            high: Number(bar.high),
            low: Number(bar.low),
            close: Number(bar.close),
            volume: 0
        }));
    }, [activeForecast, forecastStartTime]);

    const actualBars = useMemo<KLineBar[]>(() => (detail?.actualCandles || []).map(bar => ({
        time: bar.timestamp,
        open: Number(bar.open),
        high: Number(bar.high),
        low: Number(bar.low),
        close: Number(bar.close),
        volume: 0
    })), [detail]);

    const mergedActualBars = useMemo(() => {
        const byTime = new Map<number, KLineBar>();
        marketActualBars.forEach(bar => byTime.set(Number(bar.time), bar));
        // 已评价落库的数据优先于实时行情数据。
        actualBars.forEach(bar => byTime.set(Number(bar.time), bar));
        return [...byTime.values()].sort((a, b) => Number(a.time) - Number(b.time));
    }, [marketActualBars, actualBars]);

    const displayedActualBars = useMemo(
        () => [...contextBars, ...mergedActualBars],
        [contextBars, mergedActualBars]
    );

    const chartMarkPrice = useMemo(() => {
        if (currentMarkPrice === null) return null;
        const prices = [...displayedActualBars, ...predictedBars]
            .flatMap(bar => [Number(bar.low), Number(bar.high)])
            .filter(Number.isFinite);
        if (prices.length === 0) return null;
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const padding = Math.max((max - min) * 0.1, max * 0.002);
        return currentMarkPrice >= min - padding && currentMarkPrice <= max + padding
            ? currentMarkPrice
            : null;
    }, [currentMarkPrice, displayedActualBars, predictedBars]);

    const comparisonRows = useMemo(() => {
        if (!activeForecast?.bars) return [];
        const timeframeMs = getTimeframeMs(activeForecast.timeframe);
        // 已评价落库的实际K线只对应 5m 主预测；1h 视角下不展示逐根实际收盘。
        const actualByIndex = activeForecast === detail?.forecast
            ? new Map((detail.actualCandles || []).map(candle => [candle.index, candle]))
            : new Map();
        return activeForecast.bars.map(predicted => {
            const actual = actualByIndex.get(predicted.index);
            const predictedClose = Number(predicted.close);
            const actualClose = actual ? Number(actual.close) : null;
            return {
                key: predicted.index,
                index: predicted.index,
                timestamp: predicted.timestamp
                    ?? forecastStartTime + (predicted.index - 1) * timeframeMs,
                predictedClose,
                actualClose,
                error: actualClose
                    ? Math.abs(predictedClose - actualClose) / actualClose * 100
                    : null
            };
        });
    }, [activeForecast, detail, forecastStartTime]);

    const renderDetail = () => {
        if (detailLoading) {
            return <div style={{padding: 80, textAlign: 'center'}}><Spin/></div>;
        }
        if (!detail) {
            return <Empty description="暂无预测详情"/>;
        }
        const validation = validationPresentation[detail.validationStatus];
        const evaluation = evaluationPresentation[detail.evaluationStatus];
        const {warnings: forecastWarnings, errors: forecastErrors} =
            splitValidationMessages(detail.validationErrors);
        const viewDetail = {...detail, forecast: activeForecast ?? detail.forecast};
        const forecastConnector = buildForecastConnector(viewDetail, showHoldTp);
        const hasInvalidationLine = activeForecast?.invalidationPrice !== null
            && activeForecast?.invalidationPrice !== undefined;
        const hasModelPreview = canShowModelPreview(viewDetail);
        return (
            <Space direction="vertical" size={16} style={{width: '100%'}}>
                <Descriptions size="small" bordered column={{xs: 1, sm: 2, lg: 4}}>
                    <Descriptions.Item label="合约">{detail.instId}</Descriptions.Item>
                    <Descriptions.Item label="动作">
                        {detail.action} / {detail.openClose || '-'} / {detail.posSide || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="模型">{detail.modelName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="锚点价格">
                        {detail.anchorPrice ?? '-'} ({detail.anchorPriceSource || '-'})
                    </Descriptions.Item>
                    <Descriptions.Item label="实时价格">
                        <Space size={6}>
                            <Text style={{color: '#F0B429', fontWeight: 600}}>
                                {currentMarkPrice ?? '获取中...'}
                            </Text>
                            {markPriceUpdatedAt && (
                                <Text type="secondary" style={{fontSize: 11}}>
                                    {new Date(markPriceUpdatedAt).toLocaleTimeString('zh-CN')}
                                </Text>
                            )}
                            {markPriceError && (
                                <Text type="danger" style={{fontSize: 11}}>
                                    更新失败
                                </Text>
                            )}
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="预测方向">
                        {activeForecast?.direction || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="预测时间帧">
                        {activeForecast?.timeframe || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="预期持仓">
                        {activeForecast?.expectedHoldingMinutes != null
                            ? `${activeForecast.expectedHoldingMinutes} 分钟`
                            : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="目标止盈">
                        {activeForecast?.takeProfitPrice ?? detail.takeProfit ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="置信度">
                        {activeForecast?.confidence ?? '-'}%
                    </Descriptions.Item>
                    <Descriptions.Item label="预测校验">
                        <Tag color={validation.color}>{validation.label}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="跟踪状态">
                        <Tag color={evaluation.color}>{evaluation.label}</Tag>
                    </Descriptions.Item>
                </Descriptions>

                {forecastErrors.length > 0 && (
                    <Alert
                        type="error"
                        showIcon
                        message="预测校验错误"
                        description={forecastErrors.join('；')}
                    />
                )}

                {forecastWarnings.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="预测提示"
                        description={forecastWarnings.join('；')}
                    />
                )}

                {activeForecast ? (
                    <>
                        <div>
                            <Space size={20} wrap>
                                {/*<Text style={{color: '#00d97e'}}>■ 实际上涨K线</Text>*/}
                                {/*<Text style={{color: '#ff5252'}}>■ 实际下跌K线</Text>*/}
                                <Text style={{color: '#4d9fff'}}>□ 预期上涨</Text>
                                <Text style={{color: '#f5a623'}}>□ 预期下跌</Text>
                                <Tag color="gold" style={{margin: 0}}>
                                    时价：
                                    {currentMarkPrice === null
                                        ? '获取中...'
                                        : currentMarkPrice}
                                </Tag>
                                <Text type="secondary">
                                    共 {TOTAL_VISIBLE_BARS} 根，含预测 {activeForecast.horizonBars} 根
                                </Text>
                                <Segmented
                                    size="small"
                                    value={forecastTf}
                                    options={[
                                        {label: '5m', value: '5m'},
                                        {label: '1h', value: '1h'}
                                    ]}
                                    disabled={!detail.forecast1h}
                                    onChange={value => setForecastTf(value as '5m' | '1h')}
                                />
                                <Segmented
                                    size="small"
                                    value={showHoldTp ? 'show' : 'hide'}
                                    options={[
                                        {label: '持仓预期', value: 'show'},
                                        {label: '隐藏', value: 'hide'}
                                    ]}
                                    disabled={!hasModelPreview}
                                    onChange={value => setShowHoldTp(value === 'show')}
                                />
                                <Segmented
                                    size="small"
                                    value={showInvalidation ? 'show' : 'hide'}
                                    options={[
                                        {label: '预测失效', value: 'show'},
                                        {label: '隐藏', value: 'hide'}
                                    ]}
                                    disabled={!hasInvalidationLine}
                                    onChange={value => setShowInvalidation(value === 'show')}
                                />
                                <Text type="secondary" style={{fontSize: 12}}>
                                    滚轮缩放，拖动平移
                                </Text>
                            </Space>
                            <div style={{height: 360, marginTop: 8}}>
                                <LightweightCandlestickChart
                                    key={`forecast-chart-${detail.forecastId}-${forecastTf}-${showHoldTp ? 'holdtp' : 'plain'}`}
                                    data={displayedActualBars}
                                    comparisonData={predictedBars}
                                    markPrice={chartMarkPrice}
                                    markPriceColor="#F0B429"
                                    timeFrame={activeForecast.timeframe}
                                    maxVisibleBars={TOTAL_VISIBLE_BARS}
                                    // 锁住最新K线右边界，避免拖入未来空白区后无法回到预测窗口；
                                    // 数据范围内的滚轮缩放和左右平移仍然可用。
                                    lockRightEdge
                                    preserveViewportOnDataUpdate
                                    referenceLines={{
                                        forecastConnector,
                                        supportLines: detail.anchorPrice
                                        || (showInvalidation
                                            && activeForecast.invalidationPrice
                                            && detail.anchorPrice
                                            && Number(activeForecast.invalidationPrice) < Number(detail.anchorPrice))
                                            ? [
                                                ...(detail.anchorPrice !== null && detail.anchorPrice !== undefined
                                                    ? [{price: Number(detail.anchorPrice), label: '决策锚点'}]
                                                    : []),
                                                ...(showInvalidation
                                                && activeForecast.invalidationPrice
                                                && detail.anchorPrice
                                                && Number(activeForecast.invalidationPrice) < Number(detail.anchorPrice)
                                                    ? [{
                                                        price: Number(activeForecast.invalidationPrice),
                                                        label: '预测失效'
                                                    }]
                                                    : [])
                                            ]
                                            : undefined,
                                        resistanceLines: showInvalidation
                                        && activeForecast.invalidationPrice
                                        && detail.anchorPrice
                                        && Number(activeForecast.invalidationPrice) >= Number(detail.anchorPrice)
                                            ? [{
                                                price: Number(activeForecast.invalidationPrice),
                                                label: '预测失效'
                                            }]
                                            : undefined
                                    }}
                                />
                            </div>
                        </div>

                        {activeForecast.summary && (
                            <Alert type="info" message="模型走势摘要" description={activeForecast.summary}/>
                        )}
                        <Alert
                            type="warning"
                            showIcon
                            message="走势预测用于评估模型决策论点，目标止盈与持仓时间属于模型预期，不直接控制订单执行。"
                        />
                    </>
                ) : (
                    <Empty description="该动作没有有效的标准化K线预测"/>
                )}

                {activeForecast && (
                    <Table
                        size="small"
                        pagination={false}
                        dataSource={comparisonRows}
                        columns={[
                            {title: 'K线', dataIndex: 'index', width: 70},
                            {
                                title: '对应时间',
                                dataIndex: 'timestamp',
                                render: value => new Date(value).toLocaleString('zh-CN')
                            },
                            {title: '预测收盘', dataIndex: 'predictedClose'},
                            {
                                title: '实际收盘',
                                dataIndex: 'actualClose',
                                render: value => value ?? '-'
                            },
                            {
                                title: '绝对百分比误差',
                                dataIndex: 'error',
                                render: value => value === null ? '-' : `${value.toFixed(4)}%`
                            }
                        ]}
                    />
                )}

                {detail.metrics && (
                    <Row gutter={[12, 12]}>
                        <Col xs={12} md={6}>
                            <Statistic title="方向命中" value={detail.metrics.directionHit ? '是' : '否'}/>
                        </Col>
                        <Col xs={12} md={6}>
                            <Statistic
                                title="逐K方向准确率"
                                value={percent(detail.metrics.barDirectionAccuracyRatio, true)}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Statistic title="收盘价 MAPE" value={percent(detail.metrics.closeMapePct)}/>
                        </Col>
                        <Col xs={12} md={6}>
                            <Statistic
                                title="区间覆盖率"
                                value={percent(detail.metrics.closeIntervalCoverageRatio, true)}
                            />
                        </Col>
                    </Row>
                )}
            </Space>
        );
    };

    return (
        <Modal
            title="决策 K 线走势预期"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1080}
            destroyOnHidden
        >
            {error && <Alert type="error" showIcon message={error} style={{marginBottom: 12}}/>}
            {loading ? (
                <div style={{padding: 100, textAlign: 'center'}}><Spin/></div>
            ) : summaries.length === 0 ? (
                <Empty description="该决策没有K线预测记录"/>
            ) : (
                <Tabs
                    activeKey={selectedForecastId === null ? undefined : String(selectedForecastId)}
                    onChange={key => setSelectedForecastId(Number(key))}
                    items={summaries.map(summary => {
                        const evaluation = evaluationPresentation[summary.evaluationStatus];
                        return {
                            key: String(summary.forecastId),
                            label: (
                                <Space size={4}>
                                    <span>{summary.instId}</span>
                                    <Tag color={evaluation.color}>{evaluation.label}</Tag>
                                </Space>
                            ),
                            children: renderDetail()
                        };
                    })}
                />
            )}
        </Modal>
    );
};

export default KlineForecastModal;
